/**
 * Jurídico: rateio da despesa do contrato (pedido do Fabricio de 29/10). O centro de custo master
 * do contrato é a responsabilidade; o rateio diz quem recebe a despesa, por regra com vigência
 * (competência inicial). Três modalidades:
 * - sem rateio: 100% no master;
 * - por colaborador: beneficiários do cadastro oficial, por quantidade (licenças/unidades) ou
 *   valor individual; o valor de cada um vai aos centros de custo dele na competência (Dados
 *   Mestres). Colaborador sem centro na competência fica pendente, sem centro presumido;
 * - por centros de custo: vários centros oficiais, por percentual (soma 100%) ou valor (soma o
 *   total da competência).
 * Contas em centavos (bigint), sem ponto flutuante; os centavos que sobram do arredondamento vão
 * para as maiores frações (maior resto), sem mudar o total.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';

export const CONTRACT_ALLOCATION_MODES = ['none', 'employees', 'cost_centers'] as const;
export type ContractAllocationMode = (typeof CONTRACT_ALLOCATION_MODES)[number];
export const CONTRACT_ALLOCATION_BASES = {
  none: [],
  employees: ['units', 'amount'],
  cost_centers: ['percent', 'amount'],
} as const;
export type ContractAllocationBasis = 'units' | 'amount' | 'percent';

const PERCENT_RE = /^\d{1,3}(\.\d{1,2})?$/;
const AMOUNT_RE = /^\d{1,15}(\.\d{1,2})?$/;
const UNITS_RE = /^\d{1,9}(\.\d{1,4})?$/;
const optional = (re: RegExp, message: string) =>
  z
    .string()
    .trim()
    .regex(re, message)
    .or(z.literal(''))
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const contractAllocationLineInput = z.object({
  costCenterId: z.uuid().nullable().optional(),
  employeeId: z.uuid().nullable().optional(),
  percent: optional(PERCENT_RE, 'Percentual com até duas casas (ex.: 33.33)'),
  amount: optional(AMOUNT_RE, 'Valor com até duas casas (ex.: 1500.00)'),
  units: optional(UNITS_RE, 'Quantidade com até quatro casas'),
});

/** Centésimos de percentual (33.33 → 3333), sem ponto flutuante. */
function hundredths(percent: string): bigint {
  const [int, dec = ''] = percent.split('.');
  return BigInt(int!) * 100n + BigInt(dec.padEnd(2, '0'));
}

/** Nova regra de rateio a partir de uma competência (AAAA-MM). */
export const contractAllocationRuleInput = z
  .object({
    validFrom: z
      .string()
      .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Informe a competência inicial (AAAA-MM)'),
    mode: z.enum(CONTRACT_ALLOCATION_MODES),
    basis: z.enum(['units', 'amount', 'percent']).nullable().optional(),
    lines: z.array(contractAllocationLineInput).max(500).default([]),
    notes: z
      .string()
      .trim()
      .max(2000)
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
  })
  .superRefine((v, ctx) => {
    const issue = (path: (string | number)[], message: string) =>
      ctx.addIssue({ code: 'custom', path, message });
    if (v.mode === 'none') {
      if (v.lines.length > 0) issue(['lines'], 'Sem rateio não tem destinatários');
      return;
    }
    const bases: readonly string[] = CONTRACT_ALLOCATION_BASES[v.mode];
    if (!v.basis || !bases.includes(v.basis)) {
      issue(['basis'], 'Escolha a base do cálculo');
      return;
    }
    if (v.lines.length === 0) issue(['lines'], 'Informe pelo menos um destinatário');
    const target = v.mode === 'employees' ? 'employeeId' : 'costCenterId';
    const other = v.mode === 'employees' ? 'costCenterId' : 'employeeId';
    const seen = new Set<string>();
    let total = 0n;
    v.lines.forEach((l, i) => {
      const id = l[target];
      if (!id) {
        issue(
          ['lines', i, target],
          v.mode === 'employees' ? 'Escolha o colaborador' : 'Escolha o centro de custo',
        );
      } else if (seen.has(id)) {
        issue(['lines', i, target], 'Destinatário repetido');
      } else seen.add(id);
      if (l[other]) issue(['lines', i, other], 'Não se aplica a esta modalidade');
      const value = l[v.basis as 'percent' | 'amount' | 'units'];
      if (!value) {
        issue(['lines', i, v.basis!], 'Informe o valor da base');
        return;
      }
      if (v.basis === 'percent') {
        const h = hundredths(value);
        if (h <= 0n || h > 10000n) issue(['lines', i, 'percent'], 'Percentual entre 0,01 e 100');
        total += h;
      } else if (toScaled(value) <= 0n) {
        issue(['lines', i, v.basis!], 'Precisa ser maior que zero');
      }
    });
    if (v.basis === 'percent' && v.lines.length > 0 && total !== 10000n) {
      issue(
        ['lines'],
        `Os percentuais somam ${fromScaled(total * 100n).replace(/\.?0+$/, '')}%, e não 100%`,
      );
    }
  });
export type ContractAllocationRuleInput = z.infer<typeof contractAllocationRuleInput>;

/**
 * Divide `total` (centavos) pelos pesos, fechando os centavos pelo maior resto (empate: a ordem).
 * A soma do resultado é sempre igual ao total.
 */
export function distributeCents(total: bigint, weights: bigint[]): bigint[] {
  const sum = weights.reduce((a, b) => a + b, 0n);
  if (sum <= 0n) return weights.map(() => 0n);
  const base = weights.map((w) => (total * w) / sum);
  const rest = weights.map((w, i) => ({ i, r: (total * w) % sum }));
  let left = total - base.reduce((a, b) => a + b, 0n);
  rest.sort((a, b) => (b.r > a.r ? 1 : b.r < a.r ? -1 : a.i - b.i));
  for (const { i } of rest) {
    if (left <= 0n) break;
    base[i]! += 1n;
    left -= 1n;
  }
  return base;
}

const toCents = (amount: string) => toScaled(amount) / 100n;
const fromCents = (cents: bigint) => fromScaled(cents * 100n);

export interface AllocationRuleView {
  mode: ContractAllocationMode;
  basis: ContractAllocationBasis | null;
  lines: {
    costCenterId: string | null;
    employeeId: string | null;
    percent: string | null;
    amount: string | null;
    units: string | null;
  }[];
}

/**
 * Distribuição de uma competência: o valor de cada centro de custo (somando o total) e o que ficou
 * pendente (colaborador sem centro na competência). `employeeCenters` traz, para cada colaborador,
 * os centros e percentuais vigentes na competência (Dados Mestres). O master só recebe a despesa
 * quando não há rateio; nunca é somado de novo ao rateio.
 */
export function allocateCompetence(input: {
  total: string;
  masterCostCenterId: string | null;
  rule: AllocationRuleView | null;
  employeeCenters: Map<string, { costCenterId: string; percentage: string }[]>;
}):
  | {
      ok: true;
      byCostCenter: { costCenterId: string; amount: string }[];
      byRecipient: { costCenterId: string | null; employeeId: string | null; amount: string }[];
      pending: { employeeId: string; amount: string }[];
    }
  | { ok: false; code: 'no_master' | 'amount_mismatch'; difference?: string } {
  const total = toCents(input.total);
  const rule = input.rule;
  if (!rule || rule.mode === 'none') {
    if (!input.masterCostCenterId) return { ok: false, code: 'no_master' };
    const amount = fromCents(total);
    return {
      ok: true,
      byCostCenter: [{ costCenterId: input.masterCostCenterId, amount }],
      byRecipient: [{ costCenterId: input.masterCostCenterId, employeeId: null, amount }],
      pending: [],
    };
  }
  let shares: bigint[];
  if (rule.basis === 'amount') {
    shares = rule.lines.map((l) => toCents(l.amount ?? '0'));
    const sum = shares.reduce((a, b) => a + b, 0n);
    if (sum !== total) {
      return { ok: false, code: 'amount_mismatch', difference: fromCents(total - sum) };
    }
  } else {
    // Pesos na escala do banco: o percentual gravado tem quatro casas (33.3300).
    const weights = rule.lines.map((l) =>
      toScaled((rule.basis === 'percent' ? l.percent : l.units) ?? '0'),
    );
    shares = distributeCents(total, weights);
  }
  const byRecipient = rule.lines.map((l, i) => ({
    costCenterId: l.costCenterId,
    employeeId: l.employeeId,
    amount: fromCents(shares[i]!),
  }));
  const centers = new Map<string, bigint>();
  const add = (id: string, cents: bigint) => centers.set(id, (centers.get(id) ?? 0n) + cents);
  const pending: { employeeId: string; amount: string }[] = [];
  rule.lines.forEach((l, i) => {
    const share = shares[i]!;
    if (rule.mode === 'cost_centers') {
      add(l.costCenterId!, share);
      return;
    }
    const own = input.employeeCenters.get(l.employeeId!) ?? [];
    if (own.length === 0) {
      pending.push({ employeeId: l.employeeId!, amount: fromCents(share) });
      return;
    }
    const parts = distributeCents(
      share,
      own.map((c) => hundredths(trimPercent(c.percentage))),
    );
    own.forEach((c, j) => add(c.costCenterId, parts[j]!));
  });
  return {
    ok: true,
    byCostCenter: [...centers].map(([costCenterId, cents]) => ({
      costCenterId,
      amount: fromCents(cents),
    })),
    byRecipient,
    pending,
  };
}

/** Percentual do banco ("33.3300") em até duas casas, para os centésimos. */
function trimPercent(p: string): string {
  const [int, dec = ''] = p.split('.');
  return dec ? `${int}.${dec.slice(0, 2)}` : int!;
}

/** Item da compra como o Jurídico o recebe: só referências e proporções (ADR 0094). */
export interface PurchaseSplitItem {
  /** Como o item foi rateado; vazio com parcelas = rateio anterior a esta regra, por centros. */
  basis: string | null;
  /** Valor do item na proposta escolhida; sem proposta, os itens pesam igual. */
  value: string | null;
  lines: { costCenterId: string; percent: string }[];
  employees: { employeeId: string; share: string }[];
}

export interface PurchaseSplit {
  purchaseRequestId: string;
  number: number;
  masterCostCenterId: string | null;
  costCenterId: string | null;
  items: PurchaseSplitItem[];
}

export interface PurchaseAllocationRule {
  mode: 'employees' | 'cost_centers';
  basis: 'units' | 'percent';
  employeeScope: 'company' | 'group' | null;
  lines: { costCenterId?: string; employeeId?: string; percent: string }[];
  /** Itens com rateios diferentes: a regra soma as parcelas de cada item por centro de custo. */
  consolidated: boolean;
}

/** Escala que evita perder parte pequena antes do maior resto. */
const FRACTION_SCALE = 10n ** 12n;

/** Soma a parte de um item em `into`, pelo peso do item e pela proporção de cada destinatário. */
function addFractions(
  into: Map<string, bigint>,
  itemWeight: bigint,
  parts: readonly (readonly [string, bigint])[],
) {
  const sum = parts.reduce((a, [, w]) => a + w, 0n);
  if (sum <= 0n) return;
  for (const [id, w] of parts) {
    into.set(id, (into.get(id) ?? 0n) + (itemWeight * w * FRACTION_SCALE) / sum);
  }
}

/** Percentuais com quatro casas, somando exatamente 100 (maior resto); parte nula fica de fora. */
function percentShares(weights: Map<string, bigint>) {
  const ids = [...weights.keys()];
  const parts = distributeCents(
    toScaled('100'),
    ids.map((id) => weights.get(id)!),
  );
  return ids.flatMap((id, i) => (parts[i]! > 0n ? [{ id, percent: fromScaled(parts[i]!) }] : []));
}

/**
 * A regra de rateio do contrato a partir do rateio da compra (ADR 0094). Sem nenhum item rateado,
 * devolve null: o contrato segue como sempre.
 * - Todos os itens "todos da empresa" ou "todos do grupo": vale a abrangência, recalculada a cada
 *   competência.
 * - Todos por colaboradores escolhidos: cada pessoa com a parte dela, por percentual.
 * - Todos por centros: cada centro com o percentual dele.
 * - Itens diferentes entre si, ou algum sem rateio: uma regra só, por centros, somando as parcelas
 *   gravadas de cada item; o item sem rateio conta no centro da compra.
 * Com vários itens, cada um pesa pelo valor na proposta escolhida.
 */
export function purchaseAllocationRule(split: PurchaseSplit): PurchaseAllocationRule | null {
  const { items } = split;
  if (!items.some((i) => i.lines.length > 0)) return null;
  const basisOf = (i: PurchaseSplitItem) => {
    if (i.lines.length === 0) return 'none';
    if (i.basis === 'employees_selected' && i.employees.length === 0) return 'cost_centers';
    return i.basis ?? 'cost_centers';
  };
  const bases = new Set(items.map(basisOf));
  const only = bases.size === 1 ? [...bases][0]! : null;
  if (only === 'employees_company' || only === 'employees_group') {
    return {
      mode: 'employees',
      basis: 'units',
      employeeScope: only === 'employees_company' ? 'company' : 'group',
      lines: [],
      consolidated: false,
    };
  }
  const values = items.map((i) => (i.value ? toScaled(i.value) : 0n));
  const itemWeights = values.some((v) => v > 0n) ? values : items.map(() => 1n);
  const weights = new Map<string, bigint>();
  if (only === 'employees_selected') {
    items.forEach((item, n) =>
      addFractions(
        weights,
        itemWeights[n]!,
        item.employees.map((e) => [e.employeeId, toScaled(e.share)] as const),
      ),
    );
    return {
      mode: 'employees',
      basis: 'percent',
      employeeScope: null,
      lines: percentShares(weights).map(({ id, percent }) => ({ employeeId: id, percent })),
      consolidated: false,
    };
  }
  const fallback = split.costCenterId ?? split.masterCostCenterId;
  items.forEach((item, n) =>
    addFractions(
      weights,
      itemWeights[n]!,
      item.lines.length > 0
        ? item.lines.map((l) => [l.costCenterId, toScaled(l.percent)] as const)
        : fallback
          ? [[fallback, 1n] as const]
          : [],
    ),
  );
  return {
    mode: 'cost_centers',
    basis: 'percent',
    employeeScope: null,
    lines: percentShares(weights).map(({ id, percent }) => ({ costCenterId: id, percent })),
    consolidated: only === null,
  };
}
